type TipTapNode = {
  type?: string;
  content?: TipTapNode[];
  text?: string;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  attrs?: Record<string, unknown>;
};

export function tiptapToMarkdown(doc: Record<string, unknown> | null | undefined): string {
  if (!doc) return '';
  return convertChildren((doc as TipTapNode).content).trim();
}

function convertChildren(nodes: TipTapNode[] | undefined): string {
  if (!nodes) return '';
  return nodes.map(convertNode).join('');
}

function convertNode(node: TipTapNode): string {
  switch (node.type) {
    case 'paragraph':
      return convertInlines(node.content) + '\n\n';
    case 'heading': {
      const level = typeof node.attrs?.level === 'number' ? node.attrs.level : 1;
      return '#'.repeat(level) + ' ' + convertInlines(node.content) + '\n\n';
    }
    case 'bulletList':
      return (node.content ?? []).map((li) => convertListItem(li, '- ')).join('') + '\n';
    case 'orderedList':
      return (
        (node.content ?? []).map((li, i) => convertListItem(li, `${String(i + 1)}. `)).join('') +
        '\n'
      );
    case 'blockquote':
      return (
        convertChildren(node.content)
          .trim()
          .split('\n')
          .map((line) => `> ${line}`)
          .join('\n') + '\n\n'
      );
    case 'codeBlock':
      return '```\n' + extractPlainText(node) + '\n```\n\n';
    case 'horizontalRule':
      return '---\n\n';
    case 'hardBreak':
      return '\n';
    default:
      return '';
  }
}

function convertListItem(node: TipTapNode, prefix: string): string {
  const content = (node.content ?? []).map(convertNode).join('').trim();
  return prefix + content.replace(/\n\n$/, '') + '\n';
}

function convertInlines(nodes: TipTapNode[] | undefined): string {
  if (!nodes) return '';
  return nodes.map(convertInlineNode).join('');
}

function convertInlineNode(node: TipTapNode): string {
  if (node.type === 'hardBreak') return '\n';
  if (node.type !== 'text' || !node.text) return '';
  return applyMarks(node.text, node.marks);
}

function applyMarks(
  text: string,
  marks?: { type: string; attrs?: Record<string, unknown> }[],
): string {
  if (!marks || marks.length === 0) return text;
  let result = text;
  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        result = `**${result}**`;
        break;
      case 'italic':
        result = `_${result}_`;
        break;
      case 'strike':
        result = `~~${result}~~`;
        break;
      case 'code':
        result = `\`${result}\``;
        break;
      case 'link':
        result = `[${result}](${String(mark.attrs?.href ?? '')})`;
        break;
    }
  }
  return result;
}

function extractPlainText(node: TipTapNode): string {
  if (node.type === 'text' && typeof node.text === 'string') return node.text;
  if (!node.content) return '';
  return node.content.map(extractPlainText).join('\n');
}
